import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { ClipboardList, Calendar, Target, Plus, Edit, Trash2 } from 'lucide-react';

function ProductionPlan() {
  const { API } = useContext(AppContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState('current');

  useEffect(() => {
    fetchPlans();
  }, [selectedWeek]);

  const fetchPlans = async () => {
    // Mock data for demonstration
    setPlans([
      { id: '1', product: 'Strip Type A', weeklyTarget: 35000, completed: 28500, startDate: '2025-02-03', endDate: '2025-02-07', status: 'In Progress', priority: 'High' },
      { id: '2', product: 'Strip Type B', weeklyTarget: 25000, completed: 25000, startDate: '2025-02-03', endDate: '2025-02-07', status: 'Completed', priority: 'Medium' },
      { id: '3', product: 'Strip Type C', weeklyTarget: 20000, completed: 12000, startDate: '2025-02-03', endDate: '2025-02-07', status: 'In Progress', priority: 'High' },
      { id: '4', product: 'Strip Type D', weeklyTarget: 15000, completed: 0, startDate: '2025-02-10', endDate: '2025-02-14', status: 'Planned', priority: 'Low' },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Planned': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading plans...</div>;
  }

  const totalTarget = plans.reduce((sum, p) => sum + p.weeklyTarget, 0);
  const totalCompleted = plans.reduce((sum, p) => sum + p.completed, 0);

  return (
    <div className="space-y-6" data-testid="production-plan-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production Plan</h1>
          <p className="text-gray-600 mt-1">Weekly production targets and progress</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="current">Current Week</option>
            <option value="next">Next Week</option>
            <option value="previous">Previous Week</option>
          </select>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2">
            <Plus size={16} />
            <span>Add Plan</span>
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Weekly Target</p>
          <p className="text-2xl font-bold text-blue-700">{totalTarget.toLocaleString()}</p>
          <p className="text-xs text-blue-500 mt-1">units planned</p>
        </div>
        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Completed</p>
          <p className="text-2xl font-bold text-green-700">{totalCompleted.toLocaleString()}</p>
          <p className="text-xs text-green-500 mt-1">units produced</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
          <p className="text-sm text-purple-600 font-medium">Progress</p>
          <p className="text-2xl font-bold text-purple-700">{((totalCompleted / totalTarget) * 100).toFixed(1)}%</p>
          <p className="text-xs text-purple-500 mt-1">completion rate</p>
        </div>
      </div>

      {/* Plans Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <ClipboardList className="text-blue-600" size={20} />
            <h3 className="text-lg font-bold text-gray-900">Production Plans</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Target</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Completed</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{plan.product}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{plan.weeklyTarget.toLocaleString()}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 font-medium">{plan.completed.toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${Math.min((plan.completed / plan.weeklyTarget) * 100, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{((plan.completed / plan.weeklyTarget) * 100).toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{plan.startDate} - {plan.endDate}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(plan.priority)}`}>
                      {plan.priority}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}>
                      {plan.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition">
                        <Edit size={16} />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ProductionPlan;
